import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { Button, Card, CardContent, Tag, Typography } from '@volvo/vcdk-react';
import { useTranslation } from 'react-i18next';

import CoverageTable from './_components/CoverageTable';

import type { ICoverage } from '@/service/vpc/types/IUnit';
import type { ColumnDef } from '@tanstack/react-table';
import type { SystemIconId } from '@volvo/vcdk-assets';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import InformationWithCta from '@/components/InformationWithCta';
import { loaderUnitCoveragePage } from '@/loaders/unit.coverage.loader';
import CoverageExpiration from '@/routes/_authenticated/_account/fleets/$fleetId/units/$endCustomerUnitId/coverage/_components/CoverageExpiration';
import {
  getIndividualProductsQueryOptions,
  getLatestOperatingHoursQueryOptions,
  unitByIdQueryOptions,
} from '@/service/vpc/queryOptions';
import { chassis, coverage } from '@/utils';
import { dealerLocatorLink } from '@/utils/baseLinks';
import { ExpirationState } from '@/utils/coverage';

export const Route = createFileRoute('/_authenticated/_account/fleets/$fleetId/units/$endCustomerUnitId/coverage/')({
  component: UnitCoveragePage,
  errorComponent: ErrorBoundary,
  loader: loaderUnitCoveragePage,
});

export function UnitCoveragePage() {
  const { t } = useTranslation();
  const { endCustomerUnitId } = useParams({
    strict: false,
  });
  const navigate = useNavigate({ from: '/fleets/$fleetId/units/$endCustomerUnitId/coverage/' });

  const { data: unit } = useQuery(unitByIdQueryOptions({ endCustomerUnitId: endCustomerUnitId! }));
  const serialNumbers = chassis.getUnitSerialNumbers(unit) || [];

  const { data: productsData } = useQuery(getIndividualProductsQueryOptions({ serialNumbers }));
  const { data: latestOperatingHours } = useQuery(
    getLatestOperatingHoursQueryOptions({
      endcustomerunitids: unit ? [unit.endCustomerUnitId] : [],
    })
  );

  const getProductsBySerialNumber = (serialNumber: string) => {
    return productsData?.find((product) => product.serialNumber === serialNumber);
  };

  const getLatestOperatingHours = (chassisId: string | undefined) => {
    if (!chassisId) return undefined;
    const id = chassis.parseChassisId(chassisId);
    return latestOperatingHours?.find((loh) => loh.ChassisId === `${id?.chassisSeries}${id?.chassisNumber}`);
  };

  const getStartIcon = (coverageType: string): SystemIconId | undefined => {
    switch (coverageType) {
      case 'Standard':
        return 'security';
      case 'Standard Unlimited':
        return 'badge';
      case 'Components':
        return 'engine-penta';
      case 'Emission':
        return 'leaf';
      case 'Emission Unlimited':
        return undefined;
      case 'Extended':
        return 'plus-circle';
      case 'Extended Unlimited':
        return undefined;
      case 'Demo':
        return undefined;
      case 'Unknown':
        return undefined;
      default:
        return undefined;
    }
  };

  const columnsCoverage = [
    {
      accessorKey: 'coverageCode',
      id: 'type',
      header: t(($) => $.components.table.coverage_table.type),
      cell: (info) => {
        const row = info.row.original;
        const coverageType = coverage.getCoverageType(row.coverageCode);
        return (
          <Tag
            variant={row.state === ExpirationState.Active ? 'success-subtle' : 'neutral-subtle'}
            startIcon={getStartIcon(coverageType)}
          >
            {coverageType}
          </Tag>
        );
      },
    },
    {
      accessorFn: (row) => row.description,
      id: 'description',
      header: t(($) => $.components.table.coverage_table.description),
      cell: (info) => {
        const description = info.getValue<string>();
        return (
          <Typography variant="caption1" className="text-subtle">
            {coverage.getShortDescription(description)}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'expiration_hours',
      id: 'expiration_hours',
      header: t(($) => $.components.table.coverage_table.expiration_hours),
      cell: (info) => {
        const row = info.row.original;
        return (
          <CoverageExpiration
            isExpired={Boolean(row.state === ExpirationState.Expired)}
            isCloseToExpired={Boolean(row.state === ExpirationState.CloseHours)}
            isReasonForExpiration={Boolean(row.state === ExpirationState.ExpiredHours)}
            expiration={{ hours: row.coverageHours ?? 0 }}
          />
        );
      },
    },
    {
      accessorKey: 'expiration_date',
      id: 'expiration_date',
      header: t(($) => $.components.table.coverage_table.expiration_date),
      cell: (info) => {
        const row = info.row.original;
        return (
          <CoverageExpiration
            isExpired={Boolean(row.state === ExpirationState.Expired)}
            isCloseToExpired={Boolean(row.state === ExpirationState.CloseDate)}
            isReasonForExpiration={Boolean(row.state === ExpirationState.ExpiredDate)}
            expiration={{ date: row.coverageCodeExpirationDate ?? '' }}
          />
        );
      },
    },
  ] as ColumnDef<ICoverage>[];

  return (
    <div className="flex flex-col gap-6">
      <InformationWithCta
        storageKey="Coverage_InformationWithCta"
        heading={t(($) => $.pages.unit.coverage.cta.heading)}
        variant="info"
        closable={true}
        actions={
          <>
            <Button variant="secondary" href={dealerLocatorLink} target="_blank" rel="noopener noreferrer">
              {t(($) => $.pages.unit.coverage.cta.find_dealer)}
            </Button>
            <Button variant="tertiary" onClick={() => navigate({ to: '/support' })}>
              {t(($) => $.pages.unit.coverage.cta.learn_more)}
            </Button>
          </>
        }
      >
        <Typography>{t(($) => $.pages.unit.coverage.cta.message)}</Typography>
      </InformationWithCta>
      {unit?.installations.map((installation, idx) =>
        installation.driveLines.map((driveLine, dlIdx) =>
          driveLine.individualProducts.map((individualProduct, pIdx) => {
            const product = getProductsBySerialNumber(individualProduct.serialNumber);
            const chassisId = chassis.getId({
              chassisNumber: driveLine.chassisNumber,
              chassisSeries: driveLine.chassisSeries,
            });
            const operatingHours = getLatestOperatingHours(chassisId);
            const data = driveLine.coverage.map((cov) => ({
              ...cov,
              chassisSeriesNumber: chassisId,
              operatingHours: operatingHours?.Value,
              coverageHours: coverage.getCoverageHours(cov.coverageCode),
              state: coverage.getExpirationState(
                cov.coverageCode,
                cov.coverageCodeExpirationDate || '',
                operatingHours?.Value || 0
              ),
            }));
            return (
              <Card key={`${idx}-${dlIdx}-${pIdx}`}>
                <CardContent>
                  <Typography variant="caption1" className="text-subtle mb-2">
                    {installation.installationType.split(/(?=[A-Z])/).join(' ')}
                  </Typography>
                  <Typography variant="heading4">{product?.productDesignation || ''}</Typography>
                  <Typography variant="caption2">{individualProduct.serialNumber}</Typography>
                  <CoverageTable
                    key={`${chassisId}-${operatingHours ?? 'loading'}`}
                    data={data}
                    columns={columnsCoverage}
                  />
                </CardContent>
              </Card>
            );
          })
        )
      )}
    </div>
  );
}
